import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getGuestToken } from "../../slices/report/thunk";
import { embedDashboard } from "@superset-ui/embedded-sdk";

const SUPERSET_RENDER_WIDTH = 1200;
const RAKUTEN_OPERATOR_ID = "2w4dkkhVCtNTx0hORdNM3k7mNJE";

const EMSReport = () => {
  const containerRef = useRef(null);
  const outerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);
  const [scale, setScale] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const dispatch = useDispatch();
  const operatorId = localStorage.getItem("operatorId");

  useEffect(() => {
    if (!outerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const availableWidth = entry.contentRect.width;
        const mobile = availableWidth < SUPERSET_RENDER_WIDTH;
        setIsMobile(mobile);
        setScale(mobile ? availableWidth / SUPERSET_RENDER_WIDTH : 1);
      }
    });

    observer.observe(outerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      await dispatch(
        getGuestToken(
          setLoading,
          operatorId === RAKUTEN_OPERATOR_ID ? window.env.REACT_APP_RAKUTEN_SUPERSET_ID : window.env.REACT_APP_EMS_SUPERSET_ID,
          operatorId === RAKUTEN_OPERATOR_ID ? window.env.REACT_APP_RAKUTEN_SUPERSET_DATASET : window.env.REACT_APP_EMS_SUPERSET_DATASET,
          "ems"
        )
      );
      setTokenReady(true);
    };
    fetchToken();
  }, []);

  useEffect(() => {
    if (!tokenReady || !containerRef.current) return;

    const initDashboard = async () => {
      await embedDashboard({
        id: operatorId === RAKUTEN_OPERATOR_ID ? window.env.REACT_APP_RAKUTEN_SUPERSET_ID : window.env.REACT_APP_EMS_SUPERSET_ID,
        supersetDomain: window.env.REACT_APP_SUPERSET_DOMAIN,
        mountPoint: containerRef.current,
        fetchGuestToken: () => localStorage.getItem("emsGuestToken"),
        dashboardUiConfig: {
          hideTitle: false,
          hideChartControls: false,
          filters: { visible: true },
        },
      });

      const iframe = containerRef.current?.querySelector("iframe");
      if (iframe) {
        iframe.style.border = "none";
        iframe.style.display = "block";

        if (isMobile) {
          // ✅ Fixed desktop width scaled down
          // ✅ Height = viewport / scale so iframe fills full visible area
          iframe.style.width = `${SUPERSET_RENDER_WIDTH}px`;
          iframe.style.height = `${window.innerHeight / scale}px`;
        } else {
          // ✅ Desktop: fill container naturally
          iframe.style.width = "100%";
          iframe.style.height = "100%";
        }
      }
    };

    initDashboard();
  }, [tokenReady, isMobile, scale]);

  return (
    <>
      {loading && (
        <div style={styles.loadingWrapper}>
          <p>Loading dashboard...</p>
        </div>
      )}

      {/*
        ✅ height: 100vh — fills the screen
        ✅ NO overflow: hidden — iframe scrolls internally
        ✅ Sticky title/filter bar works because scroll happens inside iframe
      */}
      <div
        ref={outerRef}
        style={{
          display: loading ? "none" : "block",
          width: "100%",
          height: "100vh", // ✅ fills viewport, not forced tall
          backgroundColor: "#f5f5f5",
        }}
      >
        <div
          className="page-content"
          ref={containerRef}
          style={{
            paddingBottom: "20px",
            marginTop: isMobile ? "50px" : "",
            width: isMobile ? `${SUPERSET_RENDER_WIDTH}px` : "100%",
            height: "100%", // ✅ fills parent (100vh)
            transform: isMobile ? `scale(${scale})` : "none",
            transformOrigin: "top left",
          }}
        />
      </div>
    </>
  );
};

const styles = {
  loadingWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
  },
};

export default EMSReport;
